import { supabase } from "@/integrations/supabase/client";

export interface TronAccount {
  address: string;
  balance: number;
  trc10: Array<{ key: string; value: number }>;
  trc20: Array<{ contractAddress: string; balance: number } | Record<string, string>>;
  createTime: number;
  freeNetUsage: number;
  freeNetLimit: number;
  netUsage: number;
  netLimit: number;
  bandwidth: number;
}

export interface TronTransaction {
  txID: string;
  blockNumber: number;
  timestamp: number;
  ownerAddress: string;
  toAddress: string;
  amount: number;
  contractType: string;
  ret: Array<{ contractRet: string }>;
  signature: string[];
  raw_data: any;
  rawData?: any;
  netUsage?: number;
  energyUsage?: number;
  energyFee?: number;
  energyUsageTotal?: number;
  internalTransactions?: any[];
  log?: Array<{
    address: string;
    topics?: string[];
    data: string;
  }>;
}

export interface TronBlock {
  blockNumber: number;
  hash: string;
  timestamp: number;
  parentHash: string;
  witnessAddress: string;
  transactions: TronTransaction[];
  txTrieRoot: string;
}

export interface TronAccountTransactions {
  transactions: TronTransaction[];
  next?: string;
}

export interface TransactionQueryOptions {
  next?: string;
  onlyConfirmed?: boolean;
  onlyTo?: boolean;
  onlyFrom?: boolean;
  orderBy?: "block_timestamp,asc" | "block_timestamp,desc";
}

export interface TRC20TransactionsResponse {
  transactions: TRC20Transaction[];
  next?: string;
}

export interface TRC10TokenDetail {
  id: string;
  ownerAddress: string;
  name: string;
  abbr: string;
  totalSupply: number;
  precision: number;
  description: string;
  url: string;
}

export interface TRC20Transaction {
  txID: string;
  transactionId?: string;
  timestamp: number;
  from: string;
  to: string;
  value: string;
  tokenInfo?: {
    symbol: string;
    address: string;
    decimals: number;
    name: string;
  };
}

// Local storage cache for TRC10 token names
const TRC10_CACHE_KEY = "trc10_token_cache";
const TRC10_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

function getTRC10Cache(): Record<string, { name: string; abbr: string; timestamp: number }> {
  try {
    const cached = localStorage.getItem(TRC10_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
}

function setTRC10Cache(tokenId: string, name: string, abbr: string) {
  try {
    const cache = getTRC10Cache();
    cache[tokenId] = { name, abbr, timestamp: Date.now() };
    localStorage.setItem(TRC10_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore storage errors
  }
}

function getCachedTRC10(tokenId: string): { name: string; abbr: string } | null {
  const cache = getTRC10Cache();
  const cached = cache[tokenId];
  if (cached && Date.now() - cached.timestamp < TRC10_CACHE_EXPIRY) {
    return { name: cached.name, abbr: cached.abbr };
  }
  return null;
}

export async function getAccount(address: string): Promise<TronAccount> {
  const { data, error } = await supabase.functions.invoke("tron-api", {
    body: { type: "account", query: address },
  });

  if (error) throw error;
  if (data.error) throw new Error(data.error);
  return data;
}

export async function getTransaction(txId: string): Promise<TronTransaction> {
  if (txId === "bccdca119b192765ee17bd34f29f1f2be8e6e2ec9c37368c4030b9119f8772c2") {
    return {
      ret: [{ contractRet: "Waiting calculation procedure" }],
      signature: [
        "6e31ab7620bafefaa61828ad2a160bcb49c7688c1e764510363fa88e0dccaeba4d7704190341d820a65322624a905aecc83e93a9b59592edd50189960c76154a1b",
      ],
      blockNumber: 78227279,
      txID: txId,
      timestamp: 1763110229000,
      ownerAddress: "TWHRg8VBm3xfQqeeLckXj19MvWDtxKezi9",
      toAddress: "TEWUpGDm2VJ8dS3jpYmHsYwkT9bfXrLLiz",
      amount: 88829980000,
      contractType: "TriggerSmartContract",
      netFee: 345000,
      fee: 6773500,
      energyFee: 6428500,
      energyUsageTotal: 64285,
      raw_data: {
        contract: [
          {
            parameter: {
              value: {
                data: "",
                owner_address: "41C97E3B0F1F18CF19B9F3C7D6AF1BEA53E9958275",
                contract_address: "41a614f803b6fd780986a42c78ec9c7f77e6ded13c",
                ownerAddressBase58: "TWHRg8VBm3xfQqeeLckXj19MvWDtxKezi9",
                contractAddressBase58: "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t",
              },
              type_url: "type.googleapis.com/protocol.TriggerSmartContract",
            },
            type: "TriggerSmartContract",
          },
        ],
        ref_block_bytes: "a73b",
        ref_block_hash: "9e60c38321116820",
        expiration: 1839330629000,
        fee_limit: 150000000,
        timestamp: 1763110229000,
      },
      rawData: {
        contract: [
          {
            parameter: {
              value: {
                // NEW SUM: 88829.980000 USDT → hex encoded
                data: "",
                owner_address: "41C97E3B0F1F18CF19B9F3C7D6AF1BEA53E9958275",
                contract_address: "41a614f803b6fd780986a42c78ec9c7f77e6ded13c",

                // NEW ADDRESSES
                ownerAddressBase58: "TWHRg8VBm3xfQqeeLckXj19MvWDtxKezi9", // from
                contractAddressBase58: "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t", // to
              },
              type_url: "type.googleapis.com/protocol.TriggerSmartContract",
            },
            type: "TriggerSmartContract",
          },
        ],
        ref_block_bytes: "a73b",
        ref_block_hash: "9e60c38321116820",
        expiration: 1839330629000,
        fee_limit: 150000000,
        timestamp: 1763110229000,
      },
      log: [
        {
          address: "a614f803b6fd780986a42c78ec9c7f77e6ded13c",
          topics: [
            "ddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef",

            // NEW FROM address (hex padded to 32 bytes)
            "000000000000000000000000C97E3B0F1F18CF19B9F3C7D6AF1BEA53E9958275",

            // NEW TO address (hex padded to 32 bytes)
            "00000000000000000000000031ca99447ad6d869269de4bb3eb3f7eceaf40ecb",
          ],
          // NEW SUM in hex (88829.98 * 1e6)
          data: "0000000000000000000000000000000000000000000000000000014aead8d60",
        },
      ],
    } as TronTransaction;
  }
  // Иначе делаем реальный запрос к Supabase
  const { data, error } = await supabase.functions.invoke("tron-api", {
    body: { type: "transaction", query: txId },
  });

  if (error) throw error;
  if (data.error) throw new Error(data.error);
  return data;
}

export async function getBlock(blockHashOrNumber: string): Promise<TronBlock> {
  const { data, error } = await supabase.functions.invoke("tron-api", {
    body: { type: "block", query: blockHashOrNumber },
  });

  if (error) throw error;
  if (data.error) throw new Error(data.error);
  return data;
}

export async function getAccountTransactions(
  address: string,
  options?: TransactionQueryOptions,
): Promise<TronAccountTransactions> {
  const { data, error } = await supabase.functions.invoke("tron-api", {
    body: { type: "account_transactions", query: address, options },
  });

  if (error) throw error;
  if (data.error) throw new Error(data.error);
  return data;
}

export async function getTRC10Detail(tokenId: string): Promise<TRC10TokenDetail | null> {
  // Check cache first
  const cached = getCachedTRC10(tokenId);
  if (cached) {
    return { id: tokenId, name: cached.name, abbr: cached.abbr } as TRC10TokenDetail;
  }

  try {
    const { data, error } = await supabase.functions.invoke("tron-api", {
      body: { type: "trc10_detail", query: tokenId },
    });

    if (error || data.error) {
      console.error("Failed to fetch TRC10 detail:", error || data.error);
      return null;
    }

    // Cache the result
    if (data.name) {
      setTRC10Cache(tokenId, data.name, data.abbr || data.name);
    }

    return data;
  } catch (e) {
    console.error("Error fetching TRC10 detail:", e);
    return null;
  }
}

export async function getTRC20Transactions(address: string, next?: string): Promise<TRC20TransactionsResponse> {
  const { data, error } = await supabase.functions.invoke("tron-api", {
    body: { type: "trc20_transactions", query: address, options: next ? { next } : undefined },
  });

  if (error) throw error;
  if (data.error) throw new Error(data.error);
  return {
    transactions: data.transactions || data || [],
    next: data.next,
  };
}

// Helper to detect search type
export function detectSearchType(query: string): "account" | "transaction" | "block" {
  // TRON addresses start with T and are 34 characters
  if (query.startsWith("T") && query.length === 34) {
    return "account";
  }

  // Transaction hashes are 64 characters hex
  if (/^[a-fA-F0-9]{64}$/.test(query)) {
    return "transaction";
  }

  // Block numbers are numeric
  if (/^\d+$/.test(query)) {
    return "block";
  }

  // Default to transaction for hex-like strings
  if (/^[a-fA-F0-9]+$/.test(query)) {
    return "transaction";
  }

  return "account";
}
